/**
 * EngagementPage — data-bound Engagement tab (Plan 06 Task 6.3).
 *
 * Layout mirrors IA §3.1:
 *   - Row 1: KPI grid with 4 cards (DAU / WAU / MAU / Retention)
 *   - Row 2: UsersTrend chart full-width
 *   - Row 3: ActivityHeatmap + CohortHeatmap side-by-side (1/2 each on lg+,
 *            stacked on md-)
 *   - Row 4: Top Users SortableTable (7 columns, sortable subset)
 *
 * Every widget is wrapped in a <TabErrorBoundary> so one render failure
 * doesn't take down the tab. Each hook owns its own loading / error
 * handling — error states render an inline retry card matching
 * OverviewPage's convention.
 *
 * Sort state for the Top Users table flows through the URL via Plan 05's
 * <SortableTable tableId="topUsers">; the sort key is also forwarded to the
 * server so the query re-runs with the new ORDER BY.
 */

import { RecountNotice } from '@/components/RecountNotice';
import * as React from 'react';

import {
  useActivityHeatmap,
  useCohortHeatmap,
  useEngagementKpis,
  useJiraActivityTimeline,
  useTopUsers,
  useUsersTrend,
} from '@/hooks/use-engagement';
// Jira prefill funnel KPIs (generated / clicked / conversion) — the hook
// still lives in use-operations.ts (kept there 2026-04-24 reorg to avoid
// churning the server endpoint path `/salescoach/api/operations/jira-metrics`)
// but is consumed from the Engagement page because prefill adoption is an
// engagement signal, not an ops-health signal.
import { useJiraMetrics } from '@/hooks/use-operations';
import { useOpenUserDetail } from '@/hooks/use-open-user-detail';
import { useRestoreScroll } from '@/hooks/use-restore-scroll';
import { useUrlFilters } from '@/hooks/use-url-filters';
import {
  readTableSort,
  type Filters,
  type TableSort,
} from '@/lib/filters-schema';
import { formatCurrency } from '@/lib/format';
import { formatBucketLabel, type BucketGranularity } from '@/lib/format-bucket';
import { formatRelativeCet } from '@/lib/format-date';

import { chartPalette } from '@/lib/chart-palette';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RoleBadge } from '@/components/ui/role-badge';
import { UserLabel } from '@/components/ui/user-label';
import { ActivityHeatmap } from '@/components/ActivityHeatmap';
import { CohortHeatmap } from '@/components/CohortHeatmap';
import { CohortDrilldownModal } from '@/components/CohortDrilldownModal';
import { JiraActivityTimeline } from '@/components/JiraActivityTimeline';
import { KPICard } from '@/components/KPICard';
import { SortableTable, type SortableColumn } from '@/components/SortableTable';
import { TabErrorBoundary } from '@/components/TabErrorBoundary';
import { TraceDrillDownModal } from '@/components/TraceDrillDownModal';
import { UsersTrendBreakdownModal } from '@/components/UsersTrendBreakdownModal';
import {
  ChartTooltip,
  useChartTooltipTrigger,
  useIsTouchOnly,
} from '@/components/charts/ChartTooltip';
import {
  TimelineTotalsBadge,
  sumField,
  type TimelineTotalItem,
} from '@/components/charts/TimelineTotalsBadge';
import { InteractiveChartHint } from '@/components/charts/InteractiveChartHint';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  TopUserRow,
  TopUsersSortColumn,
  UsersTrendPoint,
} from '@/services/api-types';

// ---------------------------------------------------------------------------
// DAU target-date formatter — used by the DAU KPI card sub-label + tooltip.
// The backend emits `targetDate` as `YYYY-MM-DD` in UTC (see
// EngagementKpis.dau.targetDate). We render it as "Thu 2026-04-16" for
// the sub-label (compact + weekday-clear) and reuse the same formatting in
// the tooltip. Invalid / missing strings pass through as-is so we never
// fail to render the card when the backend hiccups.
// ---------------------------------------------------------------------------
const WEEKDAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function formatTargetDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  // Parse as UTC midnight to avoid locale-shift. `new Date('2026-04-16')`
  // is interpreted as UTC per spec, but we build it explicitly for clarity.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(d.getTime())) return iso;
  return `${WEEKDAY_ABBR[d.getUTCDay()]} ${iso}`;
}

// ---------------------------------------------------------------------------
// Inline retry card — mirrors OverviewPage's ErrorRetry
// ---------------------------------------------------------------------------

function ErrorRetry({
  label,
  onRetry,
}: {
  label: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <Card data-testid={`${label}-error`}>
      <CardContent className="p-4 space-y-2">
        <div className="text-sm font-medium text-red-600">
          {label} failed to load
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="text-xs underline text-primary hover:no-underline"
        >
          Retry
        </button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Top-users helpers
// ---------------------------------------------------------------------------

const TOP_USERS_TABLE_ID = 'topUsers';
const TOP_USERS_DEFAULT_SORT: TableSort = { column: 'requests', dir: 'desc' };
const TOP_USERS_SORTABLE_COLUMNS: ReadonlySet<string> = new Set<
  TopUsersSortColumn
>([
  'requests',
  'avgRating',
  'timeSavedHours',
  'costUsd',
  'lastActive',
  // SalesMind-merged columns. Sorted post-merge in the route layer (see
  // engagement.ts SALESMIND_SORT_COLUMNS). The table marks these headers
  // sortable, so they must be whitelisted here too — otherwise toServerSort
  // silently falls back to requests.desc and the sort arrow lies.
  'rank',
  'deltaLnav30d',
  'deltaLnav90d',
]);

/**
 * Map the URL-driven table sort back to the `<col>.<dir>` string the server
 * expects. Falls back to the default sort when the stored column isn't a
 * server-supported sort column (e.g. sorting by `userId` client-side in a
 * future revision would bypass the server ORDER BY).
 */
function toServerSort(
  stored: TableSort | undefined,
): `${TopUsersSortColumn}.${'asc' | 'desc'}` {
  const s = stored ?? TOP_USERS_DEFAULT_SORT;
  const col = TOP_USERS_SORTABLE_COLUMNS.has(s.column)
    ? (s.column as TopUsersSortColumn)
    : TOP_USERS_DEFAULT_SORT.column as TopUsersSortColumn;
  return `${col}.${s.dir}`;
}

/** Short relative-time string — delegates to CET-aware SSOT. */
function relativeTime(now: Date, then: Date): string {
  return formatRelativeCet(then.toISOString(), now.getTime());
}

/**
 * Currency with explicit sign — used for ΔLNAV columns where the polarity
 * is the point. `formatCurrency` already prefixes negatives with `-`; this
 * adds `+` for positives. Returns em-dash for null/undefined.
 */
function formatSignedCurrency(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  if (v > 0) return `+${formatCurrency(v)}`;
  return formatCurrency(v);
}

// ---------------------------------------------------------------------------
// Top-users caption — bug-C12
// ---------------------------------------------------------------------------

/**
 * Short prose caption shown above the Top Users table explaining what the
 * reader is looking at: how many users, whether the list is exhaustive, and
 * the dimension the server is sorting by. Rendering is deliberately plain
 * text (not a Tooltip) so the context is visible at a glance.
 *
 * P3-03: the previous branch was `rows.length >= fetchLimit ? 'capped' :
 * 'exhaustive'`, which over-asserts — a window that happens to have
 * exactly 100 users would read as "capped at 100" even though the list
 * IS exhaustive. Without a backend `truncated` flag or a total count we
 * can't tell the two apart, so we soften the copy to factual prose
 * ("top N users by <dimension>") and drop the "capped" wording until
 * the backend ships a truncation signal.
 *
 * TODO(audit): request a `truncated: boolean` or `total: number` field on
 * `/salescoach/api/engagement/top-users` so the caption can again distinguish
 * "exhaustive" vs "server trimmed the tail".
 */
function TopUsersCaption({
  rows,
  isLoading,
  sortColumn,
}: {
  rows: TopUserRow[];
  isLoading: boolean;
  /**
   * Kept in the prop shape for callers that still pass it, but no longer
   * consulted — the component can't reliably detect a server cap from a
   * row count alone. See TODO(audit) above.
   */
  fetchLimit?: number;
  sortColumn: string;
}): React.ReactElement {
  if (isLoading) {
    return (
      <div
        data-testid="top-users-caption-skeleton"
        className="h-4 w-80 rounded bg-muted animate-pulse"
      />
    );
  }
  const n = rows.length;
  const dimension = sortDimensionLabel(sortColumn);
  const body =
    n === 0
      ? `No users with ≥1 request in the selected window.`
      : `Top ${n} user${n === 1 ? '' : 's'} by ${dimension} in the selected window.`;
  return (
    <p
      data-testid="top-users-caption"
      className="text-xs text-muted-foreground max-w-[720px]"
    >
      {body}
    </p>
  );
}

/**
 * Map a sort-column key to human-readable English — kept local so the
 * caption reads naturally ("by average rating") without importing a larger
 * dimension-label module.
 */
function sortDimensionLabel(col: string): string {
  switch (col) {
    case 'requests':
      return 'request volume';
    case 'avgRating':
      return 'average rating';
    case 'timeSavedHours':
      return 'time saved';
    case 'costUsd':
      return 'LLM cost';
    case 'lastActive':
      return 'last active';
    default:
      return col;
  }
}

// ---------------------------------------------------------------------------
// Small, presentational chart wrappers — IA §3.1.2 / §3.1.3
// ---------------------------------------------------------------------------

function UsersTrendChart({
  data,
  loading,
  granularity,
  height = 280,
  onBarClick,
}: {
  data: UsersTrendPoint[];
  loading: boolean;
  granularity: BucketGranularity;
  height?: number;
  /**
   * Task 3: fires with the clicked bucket's ISO string when the user taps
   * either stacked segment. Optional so story / legacy callers can render
   * the chart without the drill-down wiring.
   */
  onBarClick?: (bucket: string) => void;
}): React.ReactElement {
  // Mobile tap persistence — see ChartTooltip.useChartTooltipTrigger.
  const tooltipTrigger = useChartTooltipTrigger();
  // Touch-only affordance — mirror RatingDistribution / LatencyDistribution:
  // paint a permanent stroke on clickable bars when hover isn't available,
  // since `activeBar` only fires on pointer-enter.
  const isTouchOnly = useIsTouchOnly();
  // Recharts `onClick` on <Bar> hands us `{ bucket, new, returning, ... }`
  // directly; on <BarChart> it hands us `{ activeLabel, activePayload }`.
  // Normalise both here so the caller only deals with a plain ISO string.
  const handleBarClick = React.useCallback(
    (payload: unknown) => {
      if (!onBarClick) return;
      if (payload && typeof payload === 'object') {
        const p = payload as {
          bucket?: unknown;
          activeLabel?: unknown;
          payload?: { bucket?: unknown };
        };
        const candidate =
          (typeof p.bucket === 'string' && p.bucket) ||
          (p.payload && typeof p.payload.bucket === 'string' && p.payload.bucket) ||
          (typeof p.activeLabel === 'string' && p.activeLabel);
        if (typeof candidate === 'string' && candidate.length > 0) {
          onBarClick(candidate);
        }
      }
    },
    [onBarClick],
  );
  if (loading) {
    return (
      <div
        data-testid="users-trend-skeleton"
        className="w-full rounded bg-muted animate-pulse"
        style={{ height }}
      />
    );
  }
  if (!data || data.length === 0) {
    return (
      <div
        data-testid="users-trend-empty"
        className="flex items-center justify-center w-full text-sm text-muted-foreground"
        style={{ height }}
      >
        No user activity in selected period
      </div>
    );
  }
  return (
    <div data-testid="users-trend" style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="bucket"
            tickFormatter={(v: string) => formatBucketLabel(v, granularity)}
          />
          <YAxis />
          {/* bug F1 — themed tooltip. Mobile-tap persistence via trigger switch.
               P5 — show Total row (new + returning) at the bottom of the tooltip
               so the DAU sum is visible alongside the composition.
               v1.4 dashboard-ux-fix (2026-04-24): on touch-only + drill-down,
               the Radix backdrop from BucketBreakdownModal covers the tooltip
               half a second after tap — users read it as a flash. Suppress
               the tooltip in that mode; the modal IS the detail view. Same
               pattern as RatingDistribution / LatencyDistribution / TimeSavedChart. */}
          {!(isTouchOnly && onBarClick) && (
            <Tooltip
              {...tooltipTrigger}
              cursor={false}
              content={
                <ChartTooltip
                  showTotal
                  totalLabel="DAU"
                  labelFormatter={(v) =>
                    typeof v === 'string'
                      ? formatBucketLabel(v, granularity)
                      : String(v ?? '')
                  }
                />
              }
            />
          )}
          <Legend />
          {/* New + returning stacked — together they are the DAU total.
              Task 3: both bars forward the click through `handleBarClick`
              so tapping either colour opens the per-bucket breakdown.
              `cursor: pointer` surfaces the affordance only when a handler
              is wired so test / story renders don't lie about interactivity. */}
          <Bar
            dataKey="new"
            stackId="users"
            fill={chartPalette.positiveSoft}
            name="New"
            isAnimationActive={false}
            onClick={handleBarClick}
            cursor={onBarClick ? 'pointer' : undefined}
            // v1.4 dashboard-ux-fix: foreground-stroke outline on hover so
            // the click target is visible without depending on the cursor
            // shape alone. Matches the affordance on RatingDistribution /
            // LatencyDistribution / Donut selected-slice.
            activeBar={
              onBarClick
                ? { stroke: 'hsl(var(--foreground))', strokeWidth: 1.5 }
                : false
            }
            // Touch-only fallback — stroke stays on permanently at low
            // opacity so the bar still reads as tappable without hover.
            // Only the top segment gets the stroke on stacked bars so the
            // seam with `returning` doesn't double-stroke.
            stroke={
              onBarClick && isTouchOnly ? 'hsl(var(--foreground))' : undefined
            }
            strokeWidth={onBarClick && isTouchOnly ? 1 : 0}
            strokeOpacity={onBarClick && isTouchOnly ? 0.5 : 1}
          />
          <Bar
            dataKey="returning"
            stackId="users"
            fill={chartPalette.rmRequests}
            name="Returning"
            isAnimationActive={false}
            onClick={handleBarClick}
            cursor={onBarClick ? 'pointer' : undefined}
            activeBar={
              onBarClick
                ? { stroke: 'hsl(var(--foreground))', strokeWidth: 1.5 }
                : false
            }
            stroke={
              onBarClick && isTouchOnly ? 'hsl(var(--foreground))' : undefined
            }
            strokeWidth={onBarClick && isTouchOnly ? 1 : 0}
            strokeOpacity={onBarClick && isTouchOnly ? 0.5 : 1}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

// TODO(audit): P2-02 — the top-right "Sync status unavailable" chip
// flips for ANY `/salescoach/api/**` 500, not just `/salescoach/api/sync-status`. The
// chip itself lives in DashboardLayout and reads from
// `use-sync-status.ts` (which is correctly scoped to its own endpoint).
// EngagementPage does not own that chip and cannot fix it from here.
// Move the defensive guard into DashboardLayout's render — something
// like `sync.data || sync.isError` should render "unavailable" only
// when the sync-status query itself fails, not when an unrelated
// engagement query errors out.
export function EngagementPage(): React.ReactElement {
  const { openUserDetail } = useOpenUserDetail();
  const { filters } = useUrlFilters();
  // L6 — restore scroll position when returning from the user drill-down.
  // No-op when `location.state.restoreScrollY` is absent (first visit /
  // direct link).
  useRestoreScroll();

  const kpis = useEngagementKpis(filters);
  const trend = useUsersTrend(filters, filters.granularity);
  const activityHeatmap = useActivityHeatmap(filters);
  const heatmap = useCohortHeatmap(filters);
  // Jira prefill funnel — moved here from OperationsPage 2026-04-24. The
  // KPI trio summarises "how many" (generated/clicked/conversion) while the
  // activity timeline below shows "when" (per-bucket generated + clicked).
  const jira = useJiraMetrics(filters);
  const jiraActivity = useJiraActivityTimeline(filters, filters.granularity);

  // Task 3 — users-trend bar click → per-bucket breakdown modal.
  // Hold the clicked bucket as a string (ISO timestamp the backend sent
  // back on the /users-trend payload). `null` closes the modal. The modal
  // component itself drives the fetch via `useUsersTrendBreakdown`.
  const [selectedBucket, setSelectedBucket] = React.useState<string | null>(
    null,
  );
  const handleBarClick = React.useCallback((bucket: string) => {
    setSelectedBucket(bucket);
  }, []);
  const handleModalClose = React.useCallback(() => {
    setSelectedBucket(null);
  }, []);

  // Activity heatmap cell click → TraceDrillDownModal with dow/hour extras.
  // `null` keeps the modal closed and the `useTracesList` hook inert. The
  // heatmap hardcodes `scheduled = false` on the server, so we set
  // `excludeScheduled: true` to keep the modal count matching the cell.
  const [selectedActivityCell, setSelectedActivityCell] = React.useState<
    { dow: number; hour: number } | null
  >(null);
  const handleActivityCellClick = React.useCallback(
    (dow: number, hour: number) => {
      setSelectedActivityCell({ dow, hour });
    },
    [],
  );
  const handleActivityModalClose = React.useCallback(() => {
    setSelectedActivityCell(null);
  }, []);

  // Cohort heatmap cell click → CohortDrilldownModal with cohortWeek+weekOffset.
  // `null` sentinel on both fields closes the modal; `useCohortDrilldown` stays
  // inert while either is null.
  const [selectedCohortCell, setSelectedCohortCell] = React.useState<
    { cohortWeek: string; weekOffset: number } | null
  >(null);
  const handleCohortCellClick = React.useCallback(
    (cohortWeek: string, weekOffset: number) => {
      setSelectedCohortCell({ cohortWeek, weekOffset });
    },
    [],
  );
  const handleCohortModalClose = React.useCallback(() => {
    setSelectedCohortCell(null);
  }, []);

  const topUsersSort = readTableSort(filters, TOP_USERS_TABLE_ID);
  // bug-C12: lift the fetch to the backend's maximum (100) so the table is
  // as close to exhaustive as the current API allows — backend caps limit
  // at 100 (`engagement.ts` topUsersLimitSchema). For the ~195-RM fleet this
  // means the table is exhaustive in almost every window; the caption below
  // is explicit about whether we're seeing everyone or the top-K slice.
  const TOP_USERS_FETCH_LIMIT = 100;
  const topUsers = useTopUsers(filters, {
    limit: TOP_USERS_FETCH_LIMIT,
    sort: toServerSort(topUsersSort),
  });

  const k = kpis.data;

  // Retention renders as a percentage; IA §3.1.1 says null → "—".
  const retentionCurrent =
    k?.retention.current === null || k?.retention.current === undefined
      ? null
      : k.retention.current * 100;
  const retentionDelta =
    k?.retention.delta === null || k?.retention.delta === undefined
      ? null
      : k.retention.delta;

  // BUG-6: tooltip mirrors WAU/MAU one-liner style — abbreviation +
  // single-sentence definition. Peak / most-recent numbers are surfaced
  // as the card's visible `subLabel` (below) so they stay auditable
  // without cramming the hover into three sentences.
  const dauTooltip =
    'Daily Active Users — distinct non-scheduled users on the last completed weekday strictly before the range end (Mon-selected → prior Fri; weekend-selected → prior Fri). Scheduled briefs excluded.';

  // Top-Users columns — only the server-supported ones are marked sortable.
  const columns: Array<SortableColumn<TopUserRow>> = React.useMemo(
    () => [
      {
        key: 'realName',
        header: 'Name',
        format: (_v, row) => (
          <UserLabel
            name={row.realName}
            username={row.userId}
            variant="cell"
          />
        ),
      },
      {
        key: 'userId',
        header: 'User',
        format: (v) => `@${String(v ?? '')}`,
      },
      {
        key: 'role',
        header: 'Role',
        format: (_v, row) => <RoleBadge role={row.role} />,
      },
      {
        key: 'requests',
        header: 'Requests',
        sortable: true,
        align: 'right',
      },
      {
        key: 'ratedCount',
        header: 'Rated',
        align: 'right',
      },
      {
        key: 'avgRating',
        header: 'Avg rating',
        sortable: true,
        align: 'right',
        format: (v) =>
          v === null || v === undefined ? '—' : `${(v as number).toFixed(1)}★`,
      },
      {
        key: 'timeSavedHours',
        header: 'Time saved',
        sortable: true,
        align: 'right',
        format: (v) =>
          v === null || v === undefined
            ? '—'
            : `${(v as number).toFixed(1)}h`,
      },
      {
        key: 'costUsd',
        header: 'Cost',
        sortable: true,
        align: 'right',
        format: (v) => formatCurrency(v as number | null),
      },
      {
        key: 'lastActive',
        header: 'Last active',
        sortable: true,
        align: 'right',
        format: (v) => {
          if (v === null || v === undefined) return '—';
          const d = new Date(v as string);
          return Number.isNaN(d.getTime()) ? '—' : relativeTime(new Date(), d);
        },
      },
      // SalesMind-merged columns. Null for non-RM rows (admin / evals /
      // system) and when the SalesMind dashboard fetch is degraded; both
      // render as em-dash. Sort happens in the route layer post-merge.
      {
        key: 'rank',
        header: 'Rank',
        sortable: true,
        align: 'right',
        format: (v) => (v === null || v === undefined ? '—' : String(v)),
      },
      {
        key: 'deltaLnav30d',
        header: 'Δ LNAV 30d',
        sortable: true,
        align: 'right',
        format: (v) => formatSignedCurrency(v as number | null | undefined),
      },
      {
        key: 'deltaLnav90d',
        header: 'Δ LNAV 90d',
        sortable: true,
        align: 'right',
        format: (v) => formatSignedCurrency(v as number | null | undefined),
      },
    ],
    [],
  );

  const handleRowClick = React.useCallback(
    (row: TopUserRow) => {
      if (row.userId) openUserDetail(row.userId);
    },
    [openUserDetail],
  );

  return (
    <section aria-labelledby="engagement-heading" className="space-y-6">
      <header>
        <h1 id="engagement-heading" className="text-2xl font-semibold">
          Engagement
        </h1>
        <p className="text-sm text-muted-foreground">
          Who uses the bot — DAU/WAU/MAU, cohorts, and top users.
        </p>
        <RecountNotice queries={[kpis, trend, activityHeatmap, heatmap, jira, jiraActivity, topUsers]} />
      </header>

      {/* Row 1 — KPI grid (IA §3.1.1). 5 cards on desktop (BUG-7 adds
          "New users"); 2 cards on mobile, 3 on md so the row wraps
          cleanly without squeezing a lone card onto its own line. */}
      <TabErrorBoundary name="KPI row">
        {kpis.isError ? (
          <ErrorRetry label="KPIs" onRetry={() => void kpis.refetch()} />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-4">
            <KPICard
              label="DAU"
              value={k?.dau.current ?? null}
              delta={k?.dau.delta ?? null}
              deltaCurrent={k?.dau.current ?? null}
              deltaPrevious={k?.dau.previous ?? null}
              loading={kpis.isLoading}
              // Sub-label: show the target date (so the user knows which
              // day the headline is for) + window peak/recent for
              // context. Hidden while loading.
              subLabel={
                k
                  ? [
                      k.dau.targetDate
                        ? `on ${formatTargetDate(k.dau.targetDate)}`
                        : null,
                      k.peakDau !== null || k.recentDau !== null
                        ? `peak ${k.peakDau ?? '—'} · recent ${k.recentDau ?? '—'}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ') || undefined
                  : undefined
              }
              tooltip={dauTooltip}
            />
            <KPICard
              label="WAU"
              value={k?.wau.current ?? null}
              delta={k?.wau.delta ?? null}
              deltaCurrent={k?.wau.current ?? null}
              deltaPrevious={k?.wau.previous ?? null}
              loading={kpis.isLoading}
              tooltip="Weekly Active Users — distinct users (username first, else slackId) with any non-scheduled activity in a 7-day window ending at range end. Scheduled briefs excluded."
            />
            <KPICard
              label="MAU"
              value={k?.mau.current ?? null}
              delta={k?.mau.delta ?? null}
              deltaCurrent={k?.mau.current ?? null}
              deltaPrevious={k?.mau.previous ?? null}
              loading={kpis.isLoading}
              tooltip="Monthly Active Users — distinct users (username first, else slackId) with any non-scheduled activity in a 30-day window ending at range end. Scheduled briefs excluded."
            />
            <KPICard
              label="Retention"
              value={retentionCurrent}
              format="percent"
              delta={retentionDelta}
              // Retention values are ratios (0..1) on the wire — the
              // delta pill's tooltip formats them as raw numbers, so
              // we scale to percent for parity with the headline.
              deltaCurrent={
                k?.retention.current === null ||
                k?.retention.current === undefined
                  ? null
                  : k.retention.current * 100
              }
              deltaPrevious={
                k?.retention.previous === null ||
                k?.retention.previous === undefined
                  ? null
                  : k.retention.previous * 100
              }
              // Ratios are fractional — override the default count
              // threshold (10) so small-but-valid prior cohorts still
              // surface a numeric delta.
              minPreviousSampleForDelta={3}
              loading={kpis.isLoading}
              subLabel={
                retentionCurrent === null && !kpis.isLoading
                  ? 'No prior cohort'
                  : undefined
              }
              tooltip="% of users active in the previous period of the same length who returned in the current range. Periods match your selected range (7d → prior 7d, 30d → prior 30d). Null when there's no prior cohort."
            />
            {/* BUG-7: new-user acquisition KPI. Count is users whose
                first-ever interactive trace falls inside the selected
                range; δ compares vs the preceding comparable window.
                P2-03: mobile grid is 2-col — with 5 cards the 5th
                stands alone on a trailing row. `col-span-2 md:col-span-1`
                lets it span the full row on mobile so the layout reads
                as a complete block instead of an orphan. md+ keeps the
                3-col / 5-col layout untouched. */}
            <KPICard
              className="col-span-2 md:col-span-1"
              label="New users"
              value={k?.newUsers.current ?? null}
              delta={k?.newUsers.delta ?? null}
              deltaCurrent={k?.newUsers.current ?? null}
              deltaPrevious={k?.newUsers.previous ?? null}
              loading={kpis.isLoading}
              tooltip="Users whose first INTERACTIVE (non-scheduled) trace landed inside the selected range. Earlier scheduled-brief deliveries don't disqualify them — they still count as new once they actively engage."
            />
          </div>
        )}
      </TabErrorBoundary>

      {/* O1 — DAU-trend tile removed per user request: the Users trend below
          (new/returning) already conveys DAU with composition, so the
          dedicated DAU line was redundant. */}

      {/* Row 2 — UsersTrend full-width (RM/Non-RM split now lives on
          Overview's Requests & Active Users chart; the role-stacked
          duplicate on Engagement was dropped in favour of the denser
          Activity heatmap below). IA §3.1.2 / §3.1.3. */}
      <TabErrorBoundary name="Users trend">
        {trend.isError ? (
          <ErrorRetry
            label="Users trend"
            onRetry={() => void trend.refetch()}
          />
        ) : (
          <Card>
            <CardHeader className="p-4 pb-0 flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2 space-y-0">
              <div className="flex items-center gap-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Users trend (new / returning)
                </CardTitle>
                <InteractiveChartHint data-testid="users-trend-hint" />
              </div>
              <TimelineTotalsBadge
                data-testid="users-trend-totals"
                loading={trend.isLoading}
                items={[
                  {
                    key: 'new',
                    label: 'New',
                    color: chartPalette.positiveSoft,
                    value: sumField(trend.data ?? [], 'new'),
                  },
                ] satisfies TimelineTotalItem[]}
              />
            </CardHeader>
            <CardContent className="p-4">
              <UsersTrendChart
                data={trend.data ?? []}
                loading={trend.isLoading}
                granularity={filters.granularity}
                onBarClick={handleBarClick}
              />
            </CardContent>
          </Card>
        )}
      </TabErrorBoundary>

      {/* Row 2.5 — Jira prefill funnel (moved from OperationsPage 2026-04-24).
          The KPI trio reconciles with the Slack `/report` Jira block
          (`computeJiraMetrics` — same helper powers both surfaces), and the
          activity timeline below breaks the two headline counts out over
          time so the reader can see when adoption spikes / decays.

          Narrative placement: AFTER the "who uses the bot" section (KPIs
          + users trend) and BEFORE "how they behave over weeks" (activity
          heatmap + cohort retention) — the funnel answers "do users
          actually act on the Jira links in their briefs?", which is an
          engagement signal sandwiched naturally between the volume and
          retention blocks. */}
      <section aria-labelledby="jira-prefill-heading" className="space-y-3">
        <header className="space-y-0.5">
          <h2
            id="jira-prefill-heading"
            className="text-lg font-semibold text-foreground"
          >
            Jira prefill funnel
          </h2>
          <p className="text-sm text-muted-foreground">
            Who opened / used the prefilled Jira links from their briefs.
            Generated = prefill links the bot produced; clicked = unique
            clickthroughs to Jira.
          </p>
        </header>

        <TabErrorBoundary name="Jira prefill">
          {jira.isError ? (
            <ErrorRetry
              label="Jira prefill"
              onRetry={() => void jira.refetch()}
            />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              <KPICard
                label="Jira generated"
                value={jira.data?.generated ?? null}
                format="number"
                loading={jira.isLoading}
                tooltip="Traces where the LLM produced a Jira prefill link (`jira_draft` in tool_names). Same count as shown in the Slack /report summary."
              />
              <KPICard
                label="Jira clicked"
                value={jira.data?.clicked ?? null}
                format="number"
                loading={jira.isLoading}
                tooltip="Distinct `jira_prefill_click` side-event traces (deduped by click URL). Tracks users actually clicking through to Jira from the bot's prefill link."
              />
              {/* P3-2 — when nobody has clicked yet, "0.0% · of generated
                  links clicked" reads like a hard miss on a denominator of
                  zero. Swap to a friendlier "No clicks yet" copy so the
                  zero-state surfaces the reason rather than the ratio. */}
              <KPICard
                label="Jira conversion"
                value={
                  jira.data === undefined
                    ? null
                    : jira.data.clicked === 0
                      ? 'No clicks yet'
                      : Number((jira.data.conversion * 100).toFixed(1))
                }
                format="percent"
                subLabel={
                  jira.data === undefined
                    ? undefined
                    : jira.data.clicked === 0
                      ? jira.data.generated === 0
                        ? 'No prefill links generated'
                        : `${jira.data.generated} link${jira.data.generated === 1 ? '' : 's'} generated`
                      : 'of generated links clicked'
                }
                loading={jira.isLoading}
                tooltip="clicked / generated, clamped to 100%. Same formula as the Slack /report Jira section."
              />
            </div>
          )}
        </TabErrorBoundary>

        {/* Timeline pair — same data predicates as the KPIs above, just
             bucketed. Companion-amber area = "generated" (potential),
             primary-blue line = "clicked" (conversion). The gap between
             the two signals is the un-clicked backlog. */}
        <TabErrorBoundary name="Jira activity timeline">
          {jiraActivity.isError ? (
            <ErrorRetry
              label="Jira activity timeline"
              onRetry={() => void jiraActivity.refetch()}
            />
          ) : (
            <JiraActivityTimeline
              data={jiraActivity.data ?? []}
              loading={jiraActivity.isLoading}
              granularity={filters.granularity}
            />
          )}
        </TabErrorBoundary>
      </section>

      {/* Row 3 — Activity heatmap + Weekly cohort retention, side-by-side on
          lg+ so the two 2D grids read as a pair (when does it happen vs who
          sticks around). Stacks on narrower viewports; each card keeps its
          own horizontal scroll for the inner grid. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TabErrorBoundary name="Activity heatmap">
          {activityHeatmap.isError ? (
            <ErrorRetry
              label="Activity heatmap"
              onRetry={() => void activityHeatmap.refetch()}
            />
          ) : (
            <Card>
              <CardHeader className="p-4 pb-0">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Activity heatmap
                  </CardTitle>
                  <InteractiveChartHint data-testid="activity-heatmap-hint" />
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <ActivityHeatmap
                  cells={activityHeatmap.data ?? []}
                  isLoading={activityHeatmap.isLoading}
                  dayFilter={filters.dayFilter}
                  onCellClick={handleActivityCellClick}
                />
              </CardContent>
            </Card>
          )}
        </TabErrorBoundary>

        <TabErrorBoundary name="Cohort heatmap">
          {heatmap.isError ? (
            <ErrorRetry
              label="Cohort heatmap"
              onRetry={() => void heatmap.refetch()}
            />
          ) : (
            <Card>
              <CardHeader className="p-4 pb-0">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Weekly cohort retention
                  </CardTitle>
                  <InteractiveChartHint data-testid="cohort-heatmap-hint" />
                </div>
                {/* Task 4 — expose the Task 2 scope change: the count makes
                    it visible that narrowing the preset genuinely shrinks
                    the matrix ("Showing 2 cohorts in window" on a 7d preset
                    vs the full 12 rows on a 90d preset). Hidden while the
                    hook is still loading so we never flash "Showing 0". */}
                {!heatmap.isLoading && (
                  <p
                    data-testid="cohort-heatmap-caption"
                    className="pt-1 text-[11px] text-muted-foreground"
                  >
                    Showing {heatmap.data?.length ?? 0} cohort
                    {(heatmap.data?.length ?? 0) === 1 ? '' : 's'} in window
                  </p>
                )}
              </CardHeader>
              <CardContent className="p-4">
                <CohortHeatmap
                  cohorts={heatmap.data ?? []}
                  isLoading={heatmap.isLoading}
                  onCellClick={handleCohortCellClick}
                />
              </CardContent>
            </Card>
          )}
        </TabErrorBoundary>
      </div>

      {/* Row 4 — Top Users sortable table. IA §3.1.5.
          bug-C12: caption states explicitly how many users are shown and
          whether the list is exhaustive or capped. The table scrolls
          inside a max-height wrapper once the fleet grows beyond ~12 rows
          so the page doesn't become an endless strip of rows. */}
      <TabErrorBoundary name="Top users">
        {topUsers.isError ? (
          <ErrorRetry
            label="Top users"
            onRetry={() => void topUsers.refetch()}
          />
        ) : (
          <div className="space-y-2">
            <div className="flex items-baseline justify-between gap-4">
              <div className="space-y-0.5">
                <div className="text-sm font-medium text-muted-foreground">
                  Top users
                </div>
                <TopUsersCaption
                  rows={topUsers.data ?? []}
                  isLoading={topUsers.isLoading}
                  sortColumn={
                    (topUsersSort?.column ?? TOP_USERS_DEFAULT_SORT.column) as string
                  }
                />
              </div>
            </div>
            {/* bug F4 — the sticky `<thead>` now lives inside
                <SortableTable> via the `stickyHeader` prop so the header
                row pins to the top of the same scroll container that
                caps the body height at 520px. Previously we wrapped
                the whole table in an extra `max-h` / `overflow-y-auto`
                div, which made the *table* scroll while the header
                flowed with the body and drifted out of view mid-list. */}
            <SortableTable<TopUserRow>
              tableId={TOP_USERS_TABLE_ID}
              columns={columns}
              rows={topUsers.data ?? []}
              getRowKey={(r) => r.userId}
              defaultSort={TOP_USERS_DEFAULT_SORT}
              onRowClick={handleRowClick}
              isLoading={topUsers.isLoading}
              emptyMessage="No users in selected period"
              stickyHeader
              maxHeight="max-h-[520px]"
              mobileRender={(row) => (
                <div className="space-y-2">
                  {/* Top row — name/username + role chip + chevron affordance */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <UserLabel
                        name={row.realName}
                        username={row.userId}
                        variant="stacked"
                        className="min-w-0"
                      />
                      <RoleBadge role={row.role} />
                    </div>
                    <span
                      aria-hidden
                      className="text-muted-foreground shrink-0 text-lg leading-none"
                    >
                      ›
                    </span>
                  </div>
                  {/* 2x2 stat grid — four densest metrics. Labels
                      uppercased/muted so the value leads the eye. */}
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
                    <div>
                      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        Requests
                      </dt>
                      <dd className="font-medium tabular-nums">
                        {row.requests}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        Rated
                      </dt>
                      <dd className="font-medium tabular-nums">
                        {row.ratedCount}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        Avg rating
                      </dt>
                      <dd className="font-medium tabular-nums">
                        {row.avgRating === null || row.avgRating === undefined
                          ? '—'
                          : `${row.avgRating.toFixed(1)}★`}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        Time saved
                      </dt>
                      <dd className="font-medium tabular-nums">
                        {row.timeSavedHours === null ||
                        row.timeSavedHours === undefined
                          ? '—'
                          : `${row.timeSavedHours.toFixed(1)}h`}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        Last active
                      </dt>
                      <dd className="font-medium tabular-nums">
                        {(() => {
                          if (!row.lastActive) return '—';
                          const d = new Date(row.lastActive);
                          return Number.isNaN(d.getTime())
                            ? '—'
                            : relativeTime(new Date(), d);
                        })()}
                      </dd>
                    </div>
                  </dl>
                  {/* Cost / SM footer — subtle; most operators care about
                      these secondarily compared with the volume metrics
                      above. SalesMind columns (Rank / Δ LNAV) only render
                      for RM rows; non-RM identities have no SM data. */}
                  <div className="text-xs text-muted-foreground">
                    Cost: {formatCurrency(row.costUsd ?? null)}
                    {row.rank !== null && (
                      <> · Rank: {row.rank}</>
                    )}
                    {row.deltaLnav30d !== null && (
                      <> · Δ30d: {formatSignedCurrency(row.deltaLnav30d)}</>
                    )}
                    {row.deltaLnav90d !== null && (
                      <> · Δ90d: {formatSignedCurrency(row.deltaLnav90d)}</>
                    )}
                  </div>
                </div>
              )}
            />
          </div>
        )}
      </TabErrorBoundary>

      {/* Task 3 — Users-trend bar-click drill-down. Modal is always in
          the tree; `selectedBucket === null` keeps it closed and makes
          the internal hook inert (no stray fetch). */}
      <UsersTrendBreakdownModal
        bucket={selectedBucket}
        granularity={filters.granularity}
        filters={filters}
        onClose={handleModalClose}
      />

      {/* Activity-heatmap cell drill-down. `excludeScheduled: true` so the
          modal count reconciles with the heatmap's `scheduled = false`
          hardcode. `selectedActivityCell === null` keeps it inert. */}
      <TraceDrillDownModal
        open={selectedActivityCell !== null}
        onClose={handleActivityModalClose}
        filters={filters}
        extras={
          selectedActivityCell !== null
            ? {
                dow: selectedActivityCell.dow,
                hour: selectedActivityCell.hour,
                excludeScheduled: true,
              }
            : {}
        }
        title={
          selectedActivityCell !== null
            ? `Traces on ${['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][selectedActivityCell.dow]} at ${String(selectedActivityCell.hour).padStart(2, '0')}:00 CET`
            : ''
        }
        subtitle="Every reactive trace whose CET timestamp falls in this hour-of-week cell."
        testIdPrefix="activity-heatmap-drilldown"
      />

      {/* Cohort-heatmap cell drill-down — splits the cohort into Active (had
          ≥1 reactive trace in the clicked retention week) vs Dropped (had 0).
          Reconciles against the parent cell by construction:
          len(active) == cell.activeUsers, len(active)+len(dropped) == cohortSize.
          `selectedCohortCell === null` keeps the hook inert. */}
      <CohortDrilldownModal
        cohortWeek={selectedCohortCell?.cohortWeek ?? null}
        weekOffset={selectedCohortCell?.weekOffset ?? null}
        filters={filters}
        onClose={handleCohortModalClose}
      />
    </section>
  );
}

// Re-export for type surface in tests / future helpers.
export type { Filters };
